/**
 * OperationQueue - Reliable operation queuing and batch management
 * 
 * Manages pending operations for background synchronization with
 * intelligent batching and priority handling.
 */

import { Operation, AttorneyInfo } from '../types/DocumentTypes';

interface QueuedOperation {
  operation: Operation;
  queuedAt: number;
  attempts: number;
  priority: OperationPriority;
  status: 'pending' | 'syncing' | 'synced' | 'failed';
}

type OperationPriority = 'low' | 'normal' | 'high' | 'critical';

export class OperationQueue {
  private queue: QueuedOperation[] = [];
  private maxQueueSize: number = 10000;
  private batchSize: number = 50;

  // ============================================================================
  // QUEUE OPERATIONS
  // ============================================================================

  /**
   * Add operation to queue
   */
  enqueue(operation: Operation, priority: OperationPriority = 'normal'): void {
    // Check queue size limit
    if (this.queue.length >= this.maxQueueSize) {
      console.warn('[OperationQueue] Queue full, removing oldest operations');
      this.removeOldestOperations(100);
    }
    
    const queuedOp: QueuedOperation = {
      operation,
      queuedAt: Date.now(),
      attempts: 0,
      priority,
      status: 'pending'
    };
    
    // Insert based on priority
    this.insertByPriority(queuedOp);
    
    console.log(`[OperationQueue] Enqueued ${operation.type} operation (priority: ${priority}, queue size: ${this.queue.length})`);
  }

  /**
   * Get pending operations for sync
   */
  getPendingOperations(maxCount: number = this.batchSize): Operation[] {
    const pendingOps = this.queue
      .filter(qop => qop.status === 'pending')
      .slice(0, maxCount);
    
    // Mark as syncing
    for (const qop of pendingOps) {
      qop.status = 'syncing';
      qop.attempts++;
    }
    
    return pendingOps.map(qop => qop.operation);
  }

  /**
   * Mark operations as successfully synced
   */
  markSynced(operations: Operation[]): void {
    const operationIds = new Set(operations.map(op => op.id));
    
    for (const qop of this.queue) {
      if (operationIds.has(qop.operation.id)) {
        qop.status = 'synced';
      }
    }
    
    // Remove synced operations periodically
    this.cleanupSyncedOperations();
    
    console.log(`[OperationQueue] Marked ${operations.length} operations as synced`);
  }

  /**
   * Mark operations as failed
   */
  markFailed(operations: Operation[], error: string): void {
    const operationIds = new Set(operations.map(op => op.id));
    
    for (const qop of this.queue) {
      if (operationIds.has(qop.operation.id)) {
        qop.status = 'failed';
      }
    }
    
    console.log(`[OperationQueue] Marked ${operations.length} operations as failed: ${error}`);
  }

  /**
   * Retry failed operations
   */
  retryFailedOperations(): Operation[] {
    const failedOps = this.queue.filter(qop => qop.status === 'failed');
    
    // Reset status to pending for retry
    for (const qop of failedOps) {
      qop.status = 'pending';
      qop.attempts++;
    }
    
    // Re-sort by priority
    this.sortByPriority();
    
    console.log(`[OperationQueue] Retrying ${failedOps.length} failed operations`);
    
    return failedOps.map(qop => qop.operation);
  }

  // ============================================================================
  // QUEUE MANAGEMENT
  // ============================================================================

  private insertByPriority(queuedOp: QueuedOperation): void {
    const priorityValue = this.getPriorityValue(queuedOp.priority);
    
    // Find insertion point based on priority
    let insertIndex = this.queue.length;
    
    for (let i = 0; i < this.queue.length; i++) {
      const existingPriority = this.getPriorityValue(this.queue[i].priority);
      if (priorityValue > existingPriority) {
        insertIndex = i;
        break;
      }
    }
    
    this.queue.splice(insertIndex, 0, queuedOp);
  }

  private getPriorityValue(priority: OperationPriority): number {
    switch (priority) {
      case 'critical': return 4;
      case 'high': return 3;
      case 'normal': return 2;
      case 'low': return 1;
      default: return 0;
    }
  }

  private sortByPriority(): void {
    this.queue.sort((a, b) => {
      const priorityDiff = this.getPriorityValue(b.priority) - this.getPriorityValue(a.priority);
      if (priorityDiff !== 0) return priorityDiff;
      
      // Same priority: sort by queue time (FIFO)
      return a.queuedAt - b.queuedAt;
    });
  }

  private removeOldestOperations(count: number): void {
    // Remove oldest synced operations first
    const syncedOps = this.queue.filter(qop => qop.status === 'synced');
    const toRemove = Math.min(count, syncedOps.length);
    
    for (let i = 0; i < toRemove; i++) {
      const index = this.queue.findIndex(qop => qop.status === 'synced');
      if (index !== -1) {
        this.queue.splice(index, 1);
      }
    }
    
    console.log(`[OperationQueue] Removed ${toRemove} old synced operations`);
  }

  private cleanupSyncedOperations(): void {
    // Keep only recent synced operations for audit purposes
    const cutoffTime = Date.now() - (24 * 60 * 60 * 1000); // 24 hours
    
    const beforeCount = this.queue.length;
    this.queue = this.queue.filter(qop => 
      qop.status !== 'synced' || qop.queuedAt > cutoffTime
    );
    
    const removedCount = beforeCount - this.queue.length;
    if (removedCount > 0) {
      console.log(`[OperationQueue] Cleaned up ${removedCount} old synced operations`);
    }
  }

  // ============================================================================
  // OPERATION ANALYSIS
  // ============================================================================

  /**
   * Determine operation priority based on content and context
   */
  calculateOperationPriority(operation: Operation): OperationPriority {
    // Critical: Large deletions, signature block changes
    if (operation.type === 'delete') {
      const deleteOp = operation as any;
      const deleteLength = deleteOp.end - deleteOp.start;
      if (deleteLength > 100) return 'critical';
    }
    
    // High: Operations by senior attorneys
    if (this.isSeniorAttorney(operation.attorney)) {
      return 'high';
    }
    
    // Normal: Regular text edits
    if (operation.type === 'insert' || operation.type === 'delete') {
      return 'normal';
    }
    
    // Low: Formatting changes
    return 'low';
  }

  private isSeniorAttorney(attorney: AttorneyInfo): boolean {
    return attorney.role === 'partner' || attorney.seniority > 10;
  }

  /**
   * Get operations by attorney for analysis
   */
  getOperationsByAttorney(attorneyId: string): QueuedOperation[] {
    return this.queue.filter(qop => qop.operation.attorney.id === attorneyId);
  }

  /**
   * Get operations in time range
   */
  getOperationsInTimeRange(startTime: number, endTime: number): QueuedOperation[] {
    return this.queue.filter(qop => 
      qop.queuedAt >= startTime && qop.queuedAt <= endTime
    );
  }

  // ============================================================================
  // QUEUE STATISTICS
  // ============================================================================

  /**
   * Get queue statistics
   */
  getStatistics(): {
    totalOperations: number;
    pendingCount: number;
    syncingCount: number;
    syncedCount: number;
    failedCount: number;
    averageQueueTime: number;
    operationsByType: { [type: string]: number };
  } {
    const stats = {
      totalOperations: this.queue.length,
      pendingCount: 0,
      syncingCount: 0,
      syncedCount: 0,
      failedCount: 0,
      averageQueueTime: 0,
      operationsByType: {} as { [type: string]: number }
    };
    
    let totalQueueTime = 0;
    const now = Date.now();
    
    for (const qop of this.queue) {
      // Count by status
      switch (qop.status) {
        case 'pending': stats.pendingCount++; break;
        case 'syncing': stats.syncingCount++; break;
        case 'synced': stats.syncedCount++; break;
        case 'failed': stats.failedCount++; break;
      }
      
      // Count by type
      const type = qop.operation.type;
      stats.operationsByType[type] = (stats.operationsByType[type] || 0) + 1;
      
      // Calculate queue time for pending operations
      if (qop.status === 'pending') {
        totalQueueTime += now - qop.queuedAt;
      }
    }
    
    stats.averageQueueTime = stats.pendingCount > 0 ? totalQueueTime / stats.pendingCount : 0;
    
    return stats;
  }

  /**
   * Get pending operation count
   */
  getPendingCount(): number {
    return this.queue.filter(qop => qop.status === 'pending').length;
  }

  /**
   * Check if queue is empty
   */
  isEmpty(): boolean {
    return this.getPendingCount() === 0;
  }

  /**
   * Clear all operations
   */
  clear(): void {
    this.queue = [];
    console.log('[OperationQueue] Queue cleared');
  }

  /**
   * Get queue health status
   */
  getHealthStatus(): 'healthy' | 'warning' | 'critical' {
    const stats = this.getStatistics();
    
    if (stats.failedCount > stats.totalOperations * 0.1) {
      return 'critical'; // More than 10% failed
    }
    
    if (stats.pendingCount > 100 || stats.averageQueueTime > 10000) {
      return 'warning'; // Large backlog or long queue times
    }
    
    return 'healthy';
  }
}
